/**
 * Created by rickborst on 28/02/2017.
 */

import _ from 'lodash';

import {getInstance as getApi} from 'api/api';

import * as services from './kaltura/services';

class Kaltura {

    /**
     * Constructs the adapter.
     *
     * @param {Object} config - Adapter settings.
     * @param {Object} dataSources - Data-sources instance.
     */
    constructor (config, dataSources) {

        this._dataSources = dataSources;

        this._baseUrl = config.baseUrl;
        this._endPoints = config.endPoints;
        this._presets = config.presets;
        this._defaults = config.defaults;

        this._api = getApi();

        this._persistentPayload = config.defaults.persistentPayload;

        this._configLoaded = false;

        this._addServicesToDataSources(dataSources);
    }

    /**
     * Adds services to the data-sources instance.
     *
     * @param {Object} dataSources - Data-sources instance.
     * @private
     */
    _addServicesToDataSources (dataSources) {

        _.forOwn(services, (service, member) => {

            if (dataSources[member]) {
                throw new Error('Duplicate service detected: ' + member);
            }

            dataSources[member] = service.bind(this);
        });
    }

    /**
     * Sets any request defaults.
     *
     * @param {string} action - Action name.
     * @param {Object} options - Options object.
     * @private
     */
    _getRequestDefaults (action, options) {
        let headers = options.headers || (options.headers = {});

        if (!headers['Content-Type']) {
            headers['Content-Type'] = this._defaults.contentType;
        }

        options.method = options.method || this._defaults.methods[action];

        options.data = Object.assign({}, this._persistentPayload, options.data || {});
    }

    /**
     * Returns the persistent payload.
     *
     * @returns {Object} Persistent payload.
     * @private
     */
    _getPersistentPayload () {

        return this._persistentPayload;
    }

    /**
     * Updates the persistent payload.
     *
     * @param {Object} payload - New persistent payload key->value pairs.
     * @returns {Object} Updated persistent payload.
     * @private
     */
    _updatePersistentPayload (payload = {}) {
        this._persistentPayload = Object.assign(this._persistentPayload, payload);

        return this._persistentPayload;
    }

    /**
     * Updates device id.
     *
     * @param {string} deviceID - New device ID.
     * @returns {Object} Updated config.
     * @private
     */
    _updateDeviceID (deviceID = '') {
        this._presets.udid = deviceID;

        return this;
    }

    /**
     * Applies the Kaltura configuration.
     *
     * @param {Object} currentRequestOptions - Request options object.
     * @param {Object} config - Configuration object.
     * @returns {Promise} Application promise.
     * @private
     */
    _applyConfig (currentRequestOptions = {}, config = {}) {
        let persistentPayload;

        if (this._configLoaded) {
            return Promise.resolve(this);
        }

        persistentPayload = {
            ...this._getPersistentPayload(),
            apiVersion: config.apiVersion
        };
        this._updatePersistentPayload(persistentPayload);

        if (this._baseUrl !== config.baseUrl) {

            // The base url used in the current request must be updated.
            currentRequestOptions.url = currentRequestOptions.url
                .replace(this._baseUrl, config.baseUrl);

            this._baseUrl = config.baseUrl;
        }

        this._presets = {
            ...this._presets,
            mainViewsId: config.rootCategory,
            catchupCategoryID: config.catchupCategoryID,
            discoverCategoryID: config.discoverCategoryID,
            partnerId: config.partnerId,
            playerParams: config.playerParams,
            assetTypes: config.assetTypes,
            subscriptionId: config.subscriptionId,
            responsysURL: config.responsysURL,
            onNowRetry: config.onNowRetry,
            onNowSecInterval: config.onNowSecInterval,
            landingPageConfigUrl: config.landingPageConfigUrl,
            autoStopStreamTimeout: config.autoStopStreamTimeout,
            autoStopStreamDialogTimeout: config.autoStopStreamDialogTimeout,
            gtm: config.gtm,
            switchToLive: config.switchToLive,
            removeLoaderAfter: config.removeLoaderAfter
        };

        this._configLoaded = true;
        return Promise.resolve(this);
    }

    /**
     * Kaltura create request.
     *
     * @param {Object} options - Options object.
     * @returns {Promise} Request response.
     */
    create (options) {
        this._getRequestDefaults('create', options);

        return this._dataSources.getConfig()
            .then(this._applyConfig.bind(this, options))
            .then(() => {
                return this._api.create(options);
            }).then((response) => {
                return response;
            });
    }

    /**
     * Kaltura read request.
     *
     * @param {Object} options - Options object.
     * @returns {Promise} Request response.
     */
    read (options) {
        this._getRequestDefaults('read', options);

        if (options.isConfigRequest) {
            return this._api.read(options);
        }

        return this._dataSources.getConfig()
            .then(this._applyConfig.bind(this, options))
            .then(() => {
                return this._api.read(options);
            }).then((response) => {
                return response;
            });
    }

    /**
     * Kaltura update request.
     *
     * @param {Object} options - Options object.
     * @returns {Promise} Request response.
     */
    update (options) {

        this._getRequestDefaults('update', options);

        return this._dataSources.getConfig()
            .then(this._applyConfig.bind(this, options))
            .then(() => {
                return this._api.update(options);
            }).then((response) => {
                return response;
            });
    }

    /**
     * Kaltura destroy request.
     *
     * @param {Object} options - Options object.
     * @returns {Promise} Request response.
     */
    destroy (options) {

        this._getRequestDefaults('destroy', options);

        return this._dataSources.getConfig()
            .then(this._applyConfig.bind(this, options))
            .then(() => {
                return this._api.destroy(options);
            });
    }
}

export default Kaltura;


// WEBPACK FOOTER //
// ./src/js/datasources/kaltura.js